import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { styled } from '@components-shared/src/stitches';
import { MINIMAL_IMAGE_SIZE } from '@components/Feed/ListViews/components/ReleasePill';
import { Container as MinimalUserContainerStyles } from '@components/basics/MinimalUser';
import { DeprecatedDynamicSkeleton } from '@components/main-components/skeleton/Skeleton';
import { MediaQuery } from '@context/mediaQuery';
import {
  fadeInAnimation,
  FeedListViewContainer,
  FeedListViewWrapper as MinimalWrapper,
  MOBILE_COVER_SIZE,
} from './shared';

const ANIMATION_DURATION = 0.2;
const ANIMATION_DELAY = 0;

const ANIMATION_PROPS = fadeInAnimation({ duration: ANIMATION_DURATION, delay: ANIMATION_DELAY });

const TEXT_WIDTH_SMALL = 52;
const TEXT_WIDTH_MEDIUM = 106;
const TEXT_WIDTH_LARGE = 187;
const TEXT_WIDTH_X_LARGE = 250;
const TEXT_HEIGHT_SMALL = 17;
const TEXT_HEIGHT_MEDIUM = 19;

export const TextSkeleton = ({
  width = TEXT_WIDTH_LARGE,
  height = TEXT_HEIGHT_MEDIUM,
}: {
  width?: number;
  height?: number;
}) => <DeprecatedDynamicSkeleton height={height} width={width} borderRadius={8} />;

const MinimalUserSkeleton = () => (
  <MinimalUserContainerStyles>
    <div className="min-h-[24px] min-w-[24px] flex-shrink-0 animate-pulse rounded-full bg-neutral300 md:min-h-[48px] md:min-w-[48px]" />
    <TextSkeleton width={TEXT_WIDTH_LARGE} />
  </MinimalUserContainerStyles>
);

const MinimalReleaseSkeleton = () => {
  const { tablet } = MediaQuery.useContainer();

  return (
    <div className="flex h-5 max-h-[20px] items-center justify-center gap-2 md:mx-0.5 md:h-12 md:max-h-[48px] md:gap-2.5 md:rounded-md md:border md:border-solid md:border-neutral100 md:p-[9px]">
      {tablet && (
        <DeprecatedDynamicSkeleton
          width={MINIMAL_IMAGE_SIZE}
          height={MINIMAL_IMAGE_SIZE}
          borderRadius="25%"
        />
      )}
      <TextSkeleton width={TEXT_WIDTH_MEDIUM} />
    </div>
  );
};

type RowProps = { variant?: 'collect' | 'purchase'; minimal?: boolean };

const MobileRow = () => (
  <>
    <FeedListViewContainer spaceBetween>
      <Wrapper>
        <DeprecatedDynamicSkeleton
          width={MOBILE_COVER_SIZE}
          height={MOBILE_COVER_SIZE}
          borderRadius={6}
        />
        <MobileTextWrapper>
          <TextSkeleton width={TEXT_WIDTH_X_LARGE} height={TEXT_HEIGHT_SMALL} />
        </MobileTextWrapper>
      </Wrapper>
      <TextSkeleton width={18} height={TEXT_HEIGHT_SMALL} />
    </FeedListViewContainer>
  </>
);

const DesktopRow = memo(function DesktopRow({ variant = 'collect', minimal = false }: RowProps) {
  return (
    <FeedListViewContainer>
      <Wrapper>
        <MinimalUserSkeleton />
      </Wrapper>

      {variant === 'purchase' && !minimal && (
        <Wrapper>
          <MinimalReleaseSkeleton />
        </Wrapper>
      )}

      {!minimal && (
        <VerbWrapper>
          <TextSkeleton width={TEXT_WIDTH_SMALL} />
        </VerbWrapper>
      )}

      {!minimal && (
        <Wrapper>
          <MinimalReleaseSkeleton />
        </Wrapper>
      )}

      {!minimal && (
        <Wrapper>
          <TextSkeleton />
          {variant === 'collect' && <TextSkeleton width={TEXT_WIDTH_SMALL} />}
        </Wrapper>
      )}
    </FeedListViewContainer>
  );
});

const Row = (props: RowProps) => {
  const { minimal } = props;
  const { tablet } = MediaQuery.useContainer();

  return tablet ? <DesktopRow {...props} minimal={minimal} /> : <MobileRow />;
};

type FeedListSkeletonProps = { rows?: number; minimal?: boolean };

export const FeedListSkeleton = memo(function FeedListSkeleton({
  rows = 3,
  minimal = false,
}: FeedListSkeletonProps) {
  return (
    <motion.div {...ANIMATION_PROPS}>
      {[...Array(rows)].map((_, index) => (
        <Row
          minimal={minimal}
          key={`sk-row-${index}`}
          variant={index % 3 === 0 ? 'purchase' : 'collect'}
        />
      ))}
    </motion.div>
  );
});

const Wrapper = styled(MinimalWrapper, {
  gap: 8,
  minHeight: 24,
  marginX: 0,
});

const VerbWrapper = styled('span', {
  marginX: 4,
});

const MobileTextWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});
